import { AlertTriangle, Building2, Eye, HeartPulse, Phone, Pill, ShieldCheck, Siren, Stethoscope, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../lib/utils";
import type { EmergencyCard as EmergencyCardData, EmergencyCardHospital } from "./client";

function formatCardTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function CardSection({
  children,
  icon: Icon,
  title,
  tone = "primary",
}: {
  children: ReactNode;
  icon: LucideIcon;
  title: string;
  tone?: "primary" | "rose";
}) {
  return (
    <section className="rounded-[1.5rem] border border-border bg-white p-4 shadow-soft sm:p-5">
      <h2
        className={cn(
          "flex items-center gap-2 text-sm font-bold uppercase tracking-wide",
          tone === "rose" ? "text-rose-600" : "text-primary",
        )}
      >
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function HospitalBlock({ hospital, label }: { hospital: EmergencyCardHospital; label: string }) {
  return (
    <div className="rounded-xl bg-surface px-3 py-2.5 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      {hospital.name ? <p className="mt-0.5 font-semibold text-foreground">{hospital.name}</p> : null}
      {hospital.address ? <p className="text-muted-foreground">{hospital.address}</p> : null}
      {hospital.emergency_number ? (
        <a href={`tel:${hospital.emergency_number}`} className="mt-0.5 block font-semibold text-primary">
          {hospital.emergency_number}
        </a>
      ) : null}
      {hospital.ambulance_contact_number ? (
        <p className="text-xs text-muted-foreground">Ambulance: {hospital.ambulance_contact_number}</p>
      ) : null}
    </div>
  );
}

// Shared by the authenticated preview (Workspace.tsx's Emergency Access management page) and the
// public, unauthenticated Emergency Card route (App.tsx) -- one composition-rendering path so the
// two experiences can never drift apart. This component only ever renders what `card` already
// contains; it never fetches, infers criticality, or decides what to disclose.
export function EmergencyCard({ card, variant }: { card: EmergencyCardData; variant: "preview" | "public" }) {
  const profile = card.profile;
  const hasIdentity = Boolean(profile.full_name || profile.date_of_birth || typeof profile.age === "number" || profile.blood_group);
  const hasClinical = Boolean(card.allergies?.length || card.conditions?.length || card.medications?.length);
  const hasContacts = Boolean(
    profile.emergency_contacts?.length || profile.primary_doctor || profile.preferred_hospitals?.primary || profile.preferred_hospitals?.alternate,
  );
  const hasInsurance = Boolean(profile.insurance);

  return (
    <div className="mx-auto w-full max-w-md space-y-3">
      {variant === "preview" ? (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800">
          <Eye className="size-4 shrink-0" aria-hidden="true" />
          Preview only — this does not mean Emergency Access is enabled.
        </div>
      ) : null}

      <section className="rounded-[1.5rem] border border-rose-200 bg-white p-4 shadow-soft sm:p-5">
        <div className="flex items-center gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-rose-50 text-rose-600">
            <Siren className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-rose-600">MedoPilot Emergency Card</p>
            <h1 className="truncate text-xl font-bold text-foreground">{profile.full_name ?? "Emergency Information"}</h1>
          </div>
        </div>

        {hasIdentity ? (
          <dl className="mt-4 grid grid-cols-2 gap-3">
            {profile.date_of_birth || typeof profile.age === "number" ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Age / DOB</dt>
                <dd className="mt-0.5 text-base font-semibold text-foreground">
                  {typeof profile.age === "number" ? `${profile.age} yrs` : ""}
                  {typeof profile.age === "number" && profile.date_of_birth ? " · " : ""}
                  {profile.date_of_birth ?? ""}
                </dd>
              </div>
            ) : null}
            {profile.blood_group ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blood Group</dt>
                <dd className="mt-0.5 text-base font-semibold text-foreground">{profile.blood_group}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </section>

      {hasClinical ? (
        <CardSection icon={HeartPulse} title="Critical Health Information" tone="rose">
          {card.allergies?.length ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Allergies</h3>
              <ul className="mt-1.5 space-y-1.5">
                {card.allergies.map((allergy) => (
                  <li key={allergy.allergy_id} className="flex items-start gap-2 rounded-xl bg-rose-50/70 px-3 py-2 text-sm">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-rose-600" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-foreground">{allergy.substance_name ?? "Allergy"}</span>
                      {allergy.reaction ? <span className="text-muted-foreground"> — {allergy.reaction}</span> : null}
                      {allergy.severity ? <span className="ml-1 text-xs uppercase text-rose-600">({allergy.severity})</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {card.conditions?.length ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Known Conditions</h3>
              <ul className="mt-1.5 space-y-1.5">
                {card.conditions.map((condition) => (
                  <li key={condition.condition_id} className="rounded-xl bg-surface px-3 py-2 text-sm font-semibold text-foreground">
                    {condition.condition_name ?? "Condition"}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {card.medications?.length ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Medications</h3>
              <ul className="mt-1.5 space-y-1.5">
                {card.medications.map((medication) => (
                  <li key={medication.medication_id} className="flex items-start gap-2 rounded-xl bg-surface px-3 py-2 text-sm">
                    <Pill className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-foreground">{medication.name ?? "Medication"}</span>
                      {medication.strength ? <span className="text-muted-foreground"> {medication.strength}</span> : null}
                      {medication.dose_text ? <span className="text-muted-foreground"> · {medication.dose_text}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardSection>
      ) : null}

      {hasContacts ? (
        <CardSection icon={Phone} title="Emergency / Care Contacts">
          {profile.emergency_contacts?.length ? (
            <ul className="space-y-2">
              {profile.emergency_contacts.map((contact, index) => (
                <li key={`${contact.name}-${index}`} className="rounded-xl bg-surface px-3 py-2.5 text-sm">
                  <p className="font-semibold text-foreground">
                    {contact.name}
                    {contact.relationship ? ` · ${contact.relationship}` : ""}
                  </p>
                  <a href={`tel:${contact.primary_mobile_number}`} className="mt-0.5 block font-semibold text-primary">
                    {contact.primary_mobile_number}
                  </a>
                  {contact.alternate_mobile_number ? (
                    <a href={`tel:${contact.alternate_mobile_number}`} className="block text-xs text-muted-foreground">
                      {contact.alternate_mobile_number}
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {profile.primary_doctor ? (
            <div className="rounded-xl bg-surface px-3 py-2.5 text-sm">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Stethoscope className="size-3.5" aria-hidden="true" />
                Primary Doctor
              </p>
              <p className="mt-0.5 font-semibold text-foreground">
                {profile.primary_doctor.name ?? "—"}
                {profile.primary_doctor.speciality ? ` · ${profile.primary_doctor.speciality}` : ""}
              </p>
              {profile.primary_doctor.hospital_clinic_name ? (
                <p className="text-muted-foreground">{profile.primary_doctor.hospital_clinic_name}</p>
              ) : null}
              {profile.primary_doctor.phone ? (
                <a href={`tel:${profile.primary_doctor.phone}`} className="mt-0.5 block font-semibold text-primary">
                  {profile.primary_doctor.phone}
                </a>
              ) : null}
            </div>
          ) : null}

          {profile.preferred_hospitals?.primary ? (
            <HospitalBlock label="Preferred Hospital" hospital={profile.preferred_hospitals.primary} />
          ) : null}
          {profile.preferred_hospitals?.alternate ? (
            <HospitalBlock label="Alternate Hospital" hospital={profile.preferred_hospitals.alternate} />
          ) : null}
        </CardSection>
      ) : null}

      {hasInsurance && profile.insurance ? (
        <CardSection icon={Building2} title="Insurance">
          <div className="rounded-xl bg-surface px-3 py-2.5 text-sm">
            <p className="font-semibold text-foreground">{profile.insurance.insurance_company ?? "—"}</p>
            {profile.insurance.insurance_company_contact_phone ? (
              <a href={`tel:${profile.insurance.insurance_company_contact_phone}`} className="mt-0.5 block font-semibold text-primary">
                {profile.insurance.insurance_company_contact_phone}
              </a>
            ) : null}
          </div>
        </CardSection>
      ) : null}

      <p className="flex items-start gap-2 px-1 text-xs leading-5 text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Selected by the MedoPilot account holder for emergency viewing. It may not represent their complete medical
          record.
          {card.updated_at ? ` Last updated ${formatCardTimestamp(card.updated_at)}.` : ""}
        </span>
      </p>
    </div>
  );
}
